import { useCallback, useEffect, useRef } from 'react';
import { useAuthToken, SESSION_TIMED_OUT } from './useAuthToken';
import { useLoginMutation } from './useLoginMutation';
import { IDLE_TIME_THROTTLE, SA_IDLE_TIME_THROTTLE } from '@src/constants/activity';
import { shouldSkipActivityTracking } from '@src/utils/activityUtils';
import { useUserSettings } from './useUserSettings';
import { logger } from '../utils/logger';
import {
  IDP_V2_ACCESS_EXPIRES_AT_NAME,
  IDP_V2_REFRESH_EXPIRES_AT_NAME,
  IDP_V2_REFRESH_TOKEN_NAME,
  persistIdpV2Tokens,
  refreshIdpV2Token
} from '../utils/idpV2Auth';

// Constants for storage keys
const ALL_TABS_ACTIVITY_STATE = 'op-all-tabs-activity-state'; // Key for storing all tabs' activity states - use op- prefix to avoid conflicts

const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const RETRY_REFRESH_MS = 30 * 1000;
const DEFAULT_REFRESH_DELAY_MS = 55 * 60 * 1000;

export const useAuthTokenRevalidation = () => {
  const { token, setAuthToken } = useAuthToken();
  const [, logout] = useLoginMutation();
  const refreshTimer = useRef<NodeJS.Timeout | null>(null);
  const revalidationInProgress = useRef<boolean>(false);
  const performRefreshRef = useRef<(() => void) | null>(null);

  const { autoLogoutEnabled } = useUserSettings();

  const getUserIdleState = useCallback(() => {
    let allTabsState: Record<string, any> = {};
    let idleState = 'Active';
    let idleTimeSeconds = 0;
    let isSuperAdmin = false;
    
    try {
      const storedState = localStorage.getItem(ALL_TABS_ACTIVITY_STATE);
      if (storedState) {
        allTabsState = JSON.parse(storedState);
        
        // Log the tab IDs for debugging
        if (import.meta.env.DEV) {
          logger.debug('[Token Revalidation] All tabs activity state:', {
            tabIds: Object.keys(allTabsState),
            timestamp: new Date().toISOString()
          });
        }
        
        // Find the most idle time across all tabs
        Object.values(allTabsState).forEach((tabState: any) => {
          if (tabState.state === 'Idle' && tabState.idleTimeSeconds > idleTimeSeconds) {
            idleState = 'Idle';
            idleTimeSeconds = tabState.idleTimeSeconds;
          }
          if (tabState.isSuperAdmin) {
            isSuperAdmin = true;
          }
        });
      }
    } catch (parseError) {
      logger.error('[Token Revalidation] Error parsing all tabs state:', parseError);
    }
    
    return { idleState, idleTimeSeconds, isSuperAdmin, allTabsState };
  }, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const shouldSkipRefreshForIdleUser = useCallback(() => {
    if (!autoLogoutEnabled || shouldSkipActivityTracking()) {
      return false;
    }

    const { idleState, idleTimeSeconds, isSuperAdmin } = getUserIdleState();
    if (idleState !== 'Idle' || idleTimeSeconds <= 0) {
      return false;
    }

    const idleThreshold = isSuperAdmin ? SA_IDLE_TIME_THROTTLE : IDLE_TIME_THROTTLE;
    return idleTimeSeconds >= idleThreshold - 1;
  }, [autoLogoutEnabled, getUserIdleState]);

  const scheduleRefresh = useCallback((delayMs: number) => {
    clearRefreshTimer();
    refreshTimer.current = setTimeout(() => {
      performRefreshRef.current?.();
    }, Math.max(0, delayMs));
  }, [clearRefreshTimer]);

  const performRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem(IDP_V2_REFRESH_TOKEN_NAME);
    const refreshExpiresAt = Number(localStorage.getItem(IDP_V2_REFRESH_EXPIRES_AT_NAME) || 0);
    const accessExpiresAt = Number(localStorage.getItem(IDP_V2_ACCESS_EXPIRES_AT_NAME) || 0);

    if (!token || !refreshToken) {
      clearRefreshTimer();
      return;
    }

    if (localStorage.getItem(SESSION_TIMED_OUT)) {
      clearRefreshTimer();
      return;
    }

    if (refreshExpiresAt && refreshExpiresAt <= Date.now()) {
      logger.warn('[Token Revalidation] Refresh token expired, logging out', {
        timestamp: new Date().toISOString()
      });
      logout();
      return;
    }

    if (shouldSkipRefreshForIdleUser()) {
      logger.warn('[Token Revalidation] Skipping refresh because user is idle near timeout', {
        timestamp: new Date().toISOString()
      });
      scheduleRefresh(RETRY_REFRESH_MS);
      return;
    }

    if (revalidationInProgress.current) {
      scheduleRefresh(RETRY_REFRESH_MS);
      return;
    }

    revalidationInProgress.current = true;

    try {
      const result = await refreshIdpV2Token(refreshToken);
      if (!result.access_token) {
        throw new Error('Refresh response did not include an access token');
      }

      persistIdpV2Tokens(result);
      setAuthToken(result.access_token);

      logger.info('[Token Revalidation] Refreshed IdPv2 token', {
        accessExpireDate: result.access_expire_date,
        refreshExpireDate: result.refresh_expire_date,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      const canRetryBeforeAccessExpiry = accessExpiresAt && Date.now() + RETRY_REFRESH_MS < accessExpiresAt;
      if (canRetryBeforeAccessExpiry) {
        logger.warn('[Token Revalidation] Refresh failed; retrying before access token expiry', {
          error,
          timestamp: new Date().toISOString()
        });
        scheduleRefresh(RETRY_REFRESH_MS);
      } else {
        logger.warn('[Token Revalidation] Refresh failed and access token is expired or near expiry; logging out', {
          error,
          timestamp: new Date().toISOString()
        });
        logout();
      }
    } finally {
      revalidationInProgress.current = false;
    }
  }, [clearRefreshTimer, logout, scheduleRefresh, setAuthToken, shouldSkipRefreshForIdleUser, token]);

  useEffect(() => {
    performRefreshRef.current = performRefresh;
  }, [performRefresh]);

  const scheduleNextRefresh = useCallback(() => {
    if (!token || !localStorage.getItem(IDP_V2_REFRESH_TOKEN_NAME)) {
      clearRefreshTimer();
      return;
    }

    if (localStorage.getItem(SESSION_TIMED_OUT)) {
      clearRefreshTimer();
      return;
    }

    const accessExpiresAt = Number(localStorage.getItem(IDP_V2_ACCESS_EXPIRES_AT_NAME) || 0);
    const delayMs = accessExpiresAt
      ? accessExpiresAt - Date.now() - REFRESH_BUFFER_MS
      : DEFAULT_REFRESH_DELAY_MS;

    scheduleRefresh(delayMs);
  }, [clearRefreshTimer, scheduleRefresh, token]);
  
  useEffect(() => {
    scheduleNextRefresh();

    return clearRefreshTimer;
  }, [clearRefreshTimer, scheduleNextRefresh]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleNextRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', scheduleNextRefresh);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', scheduleNextRefresh);
    };
  }, [scheduleNextRefresh]);

  useEffect(() => {
    const handleStorageChange = () => scheduleNextRefresh();

    document.addEventListener('storageChange', handleStorageChange, false);
    window.addEventListener('storage', handleStorageChange, false);

    return () => {
      document.removeEventListener('storageChange', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [scheduleNextRefresh]);

  return null;
}
